import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Heart, Coins, Eye, Users, TrendingUp } from "lucide-react";
import { appAnalytics, formatDownloads } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/Footer";
import { ANALYTICS } from "@/constants/testIds";

export default function AppAnalytics() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!user?.is_developer) { nav("/profile"); return; }
    appAnalytics(slug, days).then(setData).catch(() => setData({ error: true }));
  }, [slug, days, user, nav]);

  if (!data) return <div className="max-w-6xl mx-auto p-10 mono text-[#00F0FF] animate-pulse">Loading analytics…</div>;
  if (data.error) return <div className="max-w-6xl mx-auto p-10 text-white/70">App not found or not yours. <Link to="/dev/apps" className="text-[#00F0FF]">Back</Link></div>;

  return (
    <div data-testid={ANALYTICS.root}>
      <div className="max-w-6xl mx-auto px-6 pt-10">
        <Link to="/dev/apps" className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#00F0FF] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>
        <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// analytics · last {days} days</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter">{slug}</h1>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`mono text-xs uppercase tracking-widest px-3 py-2 rounded-full border transition-colors ${
                  days === d ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "text-white/70 border-white/10 hover:border-white/30"
                }`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div data-testid={ANALYTICS.totals} className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          <Total label="Launches" value={data.totals.installs_period} accent="#00F0FF" icon={<Download className="w-4 h-4" />} />
          <Total label="Views" value={data.totals.views_period} accent="#39FF14" icon={<Eye className="w-4 h-4" />} />
          <Total label="Likes" value={data.totals.likes} accent="#FF003C" icon={<Heart className="w-4 h-4" />} />
          <Total label="Tips" value={data.totals.tips} accent="#FFE300" icon={<Coins className="w-4 h-4" />} />
          <Total label="Comments" value={data.totals.comments} accent="#00F0FF" icon={<TrendingUp className="w-4 h-4" />} />
          <Total label="Avg DAU" value={data.totals.dau_avg} accent="#39FF14" icon={<Users className="w-4 h-4" />} />
        </div>

        {/* Charts */}
        <div data-testid={ANALYTICS.chart} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <ChartCard title="Installs" series={data.series.installs} color="#00F0FF" />
          <ChartCard title="Views" series={data.series.views} color="#39FF14" />
          <ChartCard title="Likes" series={data.series.likes} color="#FF003C" />
          <ChartCard title="Tips (coins)" series={data.series.tips} color="#FFE300" />
          <ChartCard title="Daily Active Users" series={data.series.dau} color="#00F0FF" full />
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Total({ label, value, accent, icon }) {
  return (
    <div className="glass rounded-2xl p-4" data-testid={ANALYTICS.card}>
      <div className="mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
        <span style={{ color: accent }}>{icon}</span>{label}
      </div>
      <div className="font-heading font-black text-3xl mt-2" style={{ color: accent }}>
        {typeof value === "number" ? formatDownloads(value) : value}
      </div>
    </div>
  );
}

function ChartCard({ title, series, color, full }) {
  const max = Math.max(1, ...series.map((s) => s.value));
  const w = 480, h = 140, pad = 8;
  const stepX = (w - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((s, i) => [pad + i * stepX, pad + (h - pad * 2) * (1 - s.value / max)]);
  const path = points.reduce((acc, [x, y], i) => acc + (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`), "");
  const area = path + ` L ${points[points.length - 1][0]} ${h - pad} L ${pad} ${h - pad} Z`;
  const total = series.reduce((a, s) => a + s.value, 0);
  const last = series[series.length - 1]?.value || 0;

  return (
    <div className={`glass rounded-2xl p-5 ${full ? "md:col-span-2" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/50">{title}</div>
          <div className="font-heading font-black text-2xl mt-0.5" style={{ color }}>{total.toLocaleString()}</div>
          <div className="mono text-[10px] text-white/40">Today: {last}</div>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
        <defs>
          <linearGradient id={`g-${title}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#g-${title})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.6" fill={color} opacity={i === points.length - 1 ? 1 : 0.5} />
        ))}
      </svg>
      <div className="flex justify-between mono text-[9px] text-white/30 mt-1">
        <span>{series[0]?.date?.slice(5)}</span>
        <span>{series[series.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}
